import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { Modal, ModalProps } from '@/modules/common/components/Modal';
import { useEmployeeMutation } from '@/modules/common/hooks/useEmployer';
import { moneyToInteger } from '@/modules/common/lib/moneyHandler';
import { ApiErrorResponse } from '@/types/api';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, TextField, TextFieldProps } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

const FORM_SCHEMA = yup.object().shape({
  learningBudget: yup.number().required('learningBudget is required'),
});

type EmployeeLearningBudgetDialog = Omit<
  ModalProps,
  'children' | 'title' | 'subTitle'
> & {
  employee: string;
  onSuccess: (learningBudget?: number) => void;
};

export function EmployeeLearningBudgetDialog({
  onSuccess,
  ...props
}: EmployeeLearningBudgetDialog) {
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<{ learningBudget: number }>({
    resolver: yupResolver(FORM_SCHEMA),
    defaultValues: { learningBudget: undefined },
  });

  const userContext = useCurrentUserContext();
  const { mutateAsync: updateEmployee, isPending } = useEmployeeMutation();

  const handleSetLearningBudget = async ({
    learningBudget,
  }: {
    learningBudget: number;
  }) => {
    try {
      await updateEmployee({
        id: userContext?.employer?._id ?? '',
        employeeId: props.employee,
        updatedData: {
          learningBudget: moneyToInteger(learningBudget.toString()),
        },
      });
      onSuccess(learningBudget);
      enqueueSnackbar(`Budget set to ${learningBudget} successfuly`, {
        variant: 'success',
      });
    } catch (error) {
      if ((error as ApiErrorResponse).statusCode === 400) {
        return setError('root.serverError', {
          type: 'manual',
          message:
            (error as ApiErrorResponse).message ??
            'An error occurred. Please try again later.',
        });
      }
      enqueueSnackbar('An error occurred. Please try again later.', {
        variant: 'error',
      });
    }
  };

  const handleCancel = () => {
    props.onClose?.({}, 'backdropClick');
  };

  return (
    <Modal
      {...props}
      title={`Set Employee Learning Budget limit`}
      subTitle="Allocate learning budget limit for a specific employee that will have priority over team or company defined budget limits"
      onClose={(...args) => {
        reset();
        props.onClose?.(...args);
      }}
      classes={{
        paper: 'p-8 pb-3 mx-auto max-w-xl',
      }}
    >
      <form
        onSubmit={handleSubmit(handleSetLearningBudget)}
        className="flex flex-col gap-3 justify-end p-2"
      >
        {errors.root?.serverError?.message && (
          <ul className="list-disc text-red-500">
            {errors.root.serverError.message?.split(';').map((error, index) => (
              <li key={index}>
                {error}
                <br />
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-col gap-6">
          <div>
            <Controller
              name="learningBudget"
              control={control}
              render={({ field }) => (
                <TextField
                  {...(field as TextFieldProps)}
                  error={!!errors.learningBudget}
                  helperText={errors.learningBudget?.message}
                  fullWidth
                  label="Learning Budget"
                  type="number"
                  inputProps={{
                    step: 1,
                    pattern: '\\d*',
                    min: 0,
                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (
                        e.key === '.' ||
                        e.key === '+' ||
                        e.key === '-' ||
                        e.key === 'e'
                      ) {
                        e.preventDefault();
                      }
                    },
                  }}
                />
              )}
            />
          </div>
        </div>
        <hr className="absolute right-0 left-0 mb-14" />
        <div className="flex justify-end mt-14 space-x-4">
          <Button
            size="small"
            variant="outlined"
            type="button"
            onClick={handleCancel}
            className="mt-6 w-32"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            type="submit"
            className="mt-6 w-32"
            loading={isPending}
          >
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
